import { z } from "zod"
import {
  pimWorkflowListQuerySchema,
  pimWorkflowListSchema,
  pimWorkflowReceiptSchema,
} from "./pim-workflow-history"

export const NUTRITION_QUEUE_PERIOD = { day: "24h", week: "7d" } as const
export const nutritionQueuePeriodSchema = z.enum(Object.values(NUTRITION_QUEUE_PERIOD))
export const NUTRITION_QUEUE_WINDOWS = {
  [NUTRITION_QUEUE_PERIOD.day]: { hours: 24, bucketHours: 1, label: "24 hours" },
  [NUTRITION_QUEUE_PERIOD.week]: { hours: 168, bucketHours: 24, label: "7 days" },
} as const
export const NUTRITION_QUEUE_GROUP = {
  inProgress: "in_progress",
  needsAttention: "needs_attention",
  done: "done",
} as const
export const NUTRITION_QUEUE_OUTCOME = {
  processing: "processing",
  imagesPublished: "images_published",
  imagesReady: "images_ready",
  noChange: "no_change",
  withdrawn: "withdrawn",
  unsupported: "unsupported",
  superseded: "superseded",
  unknown: "unknown",
  rejected: "rejected",
  failed: "failed",
  paused: "paused",
  delayed: "delayed",
} as const
export const NUTRITION_QUEUE_STEP = {
  received: "received",
  generation: "generation",
  upload: "upload",
  publication: "publication",
} as const
const groupSchema = z.enum(Object.values(NUTRITION_QUEUE_GROUP))
const stepSchema = z.enum(Object.values(NUTRITION_QUEUE_STEP))
const count = z.number().int().nonnegative()
export const nutritionQueueQuerySchema = z.object({
  period: nutritionQueuePeriodSchema.default(NUTRITION_QUEUE_PERIOD.week),
  search: z.string().trim().max(128).optional(),
  view: z.enum(["all", ...Object.values(NUTRITION_QUEUE_GROUP)]).default("all"),
  step: stepSchema.optional(),
  before: pimWorkflowListQuerySchema.shape.before,
  limit: z.coerce.number().int().min(1).max(100).default(25),
})
export const nutritionQueueReceiptSchema = z.object({
  receipt: pimWorkflowReceiptSchema,
  productName: z.string().nullable(),
  stores: z.array(z.string()),
  skus: z.array(z.string()),
  group: groupSchema,
  outcome: z.enum(Object.values(NUTRITION_QUEUE_OUTCOME)),
  step: stepSchema,
  imageCompleteAt: z.string().nullable(),
  imageElapsedMs: z.number().nonnegative().nullable(),
  publicationWaitingMs: z.number().nonnegative().nullable(),
})
export const nutritionQueueImageSchema = z.object({
  transitionId: z.string(),
  productName: z.string().nullable(),
  receiptId: z.number().int(),
  store: z.string(),
  sku: z.string(),
  imageId: z.number().int().nullable(),
  url: z.string().nullable(),
  recordedAt: z.string(),
  outboundEventId: z.string().nullable(),
  outboundRevision: z.number().int().nullable(),
  cause: z.string().nullable(),
  selectedNow: z.boolean(),
  freshness: z.literal("unknown"),
  objectAvailability: z.literal("unknown"),
})
export const nutritionQueueSchema = z.object({
  receipts: z.array(nutritionQueueReceiptSchema),
  nextCursor: z.string().nullable(),
  observedAt: z.string(),
  cohort: z.object({ from: z.string(), to: z.string() }),
  counts: z.object({ all: count, inProgress: count, needsAttention: count, done: count }),
  pipeline: z.object({ received: count, generation: count, upload: count, publication: count }),
  pipelineTotals: z.object({
    acceptedReceipts: count,
    rejectedReceipts: count,
    actualScopes: count,
    currentScopes: count,
    publicationAcceptedScopes: count,
    publicationWaitingScopes: count,
    generationPendingScopes: count,
    generationFailedScopes: count,
  }),
  metrics: z.object({
    received: count,
    imagesReadyAndPublished: count,
    imagesMadeCurrent: count,
    unclassifiableRejected: count,
    imageTiming: z.object({
      medianMs: z.number().nullable(),
      p95Ms: z.number().nullable(),
      samples: count,
      excluded: count,
      withinTarget: count,
      valuesMs: z.array(z.number()).max(100),
    }),
    outcomes: z.record(z.string(), count),
    groups: z.object({ inProgress: count, needsAttention: count, done: count }),
  }),
  recentImages: z.array(nutritionQueueImageSchema).max(12),
  activity: z.array(z.object({ from: z.string(), to: z.string(), imagesMadeCurrent: count })),
  controls: pimWorkflowListSchema.shape.controls,
})
export type NutritionQueueQuery = z.infer<typeof nutritionQueueQuerySchema>
export type NutritionQueue = z.infer<typeof nutritionQueueSchema>
export type NutritionQueueReceipt = z.infer<typeof nutritionQueueReceiptSchema>
export type NutritionQueueImage = z.infer<typeof nutritionQueueImageSchema>
